import { useWindowEvent } from '@/hooks/useWindowEvent';
import { useCallback, useEffect, useRef } from 'react';
import { fragmentShaderSource, vertexShaderSource } from './assistantAnimationShaders';

// Canvas styles for full coverage animation overlay

const styles = {
  canvas: {
    position: 'absolute',
    inset: 0,
    height: '100%',
    width: '100%',
    pointerEvents: 'none',
  },
};

/**
 * Creates and links a WebGL shader program from vertex and fragment shaders
 * @param {WebGLRenderingContext} gl - WebGL context
 * @param {WebGLShader} vertexShader - Compiled vertex shader
 * @param {WebGLShader} fragmentShader - Compiled fragment shader
 * @returns {WebGLProgram|null} Linked program or null if failed
 */
const createProgram = (gl, vertexShader, fragmentShader) => {
  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  // Check if program linking was successful
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
};

/**
 * Creates and compiles a WebGL shader from source code
 * @param {WebGLRenderingContext} gl - WebGL context
 * @param {number} type - Shader type (gl.VERTEX_SHADER or gl.FRAGMENT_SHADER)
 * @param {string} source - Shader source code
 * @returns {WebGLShader|null} Compiled shader or null if failed
 */
const createShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  // Check if shader compilation was successful
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
};

/**
 * Renders a single animation frame with WebGL
 * @param {WebGLRenderingContext} gl - WebGL context
 * @param {WebGLBuffer} positionBuffer - Buffer containing vertex positions
 * @param {WebGLUniformLocation} timeLocation - Uniform location for time
 * @param {number} time - Current animation time
 * @param {WebGLUniformLocation} resolutionLocation - Uniform location for resolution
 * @param {number} positionLocation - Attribute location for vertex positions
 * @param {HTMLCanvasElement} canvas - Canvas element for dimensions
 */
const animate = (
  gl,
  positionBuffer,
  timeLocation,
  time,
  resolutionLocation,
  positionLocation,
  canvas,
) => {
  // Clear canvas with transparent background
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  // Enable alpha blending for transparency
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  // Set shader uniforms
  gl.uniform1f(timeLocation, time);
  gl.uniform2f(resolutionLocation, canvas.width, canvas.height);

  // Bind vertex data and configure attribute
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

  // Draw the quad as triangle strip
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
};

/**
 * Canvas component that renders WebGL animation
 * @param {boolean} onlyFrame - Whether to render only frame outline
 * @param {number} frameWidth - Width of the frame border
 * @param {Object} sx - Additional styles to apply
 */
const CanvasWithAnimation = ({ onlyFrame = false, frameWidth = 0.0, sx = {} }) => {
  const canvasRef = useRef(null);
  const glRef = useRef(null);

  // Resize canvas to match its display size and update WebGL viewport
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const gl = glRef.current;
    if (!canvas || !gl) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
  }, []);

  // Listen for window resize events
  useWindowEvent('resize', resizeCanvas);

  // Initialize WebGL context and start animation loop
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    // Exit early if WebGL is not supported
    if (!gl) {
      return;
    }

    glRef.current = gl;
    let time = 0;

    // Create and compile shaders
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource(onlyFrame));
    const program = createProgram(gl, vertexShader, fragmentShader);

    // Define fullscreen quad vertices (normalized device coordinates)
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    // Create and populate vertex buffer
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Get shader attribute and uniform locations
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const frameWidthLocation = gl.getUniformLocation(program, 'u_frameWidth');

    gl.useProgram(program);

    // Set frame width uniform if rendering only frame
    if (onlyFrame) {
      gl.uniform1f(frameWidthLocation, frameWidth);
    }

    // Animation loop function
    const startAnimation = () => {
      animate(gl, positionBuffer, timeLocation, time, resolutionLocation, positionLocation, canvas);
      time += 0.02; // Animation speed
      window.requestAnimationFrame(startAnimation);
    };

    startAnimation();
  }, [frameWidth, onlyFrame]);

  // Initial canvas resize
  useEffect(() => {
    resizeCanvas();
  }, [resizeCanvas]);

  return <canvas ref={canvasRef} style={{ ...styles.canvas, ...sx }} />;
};

/**
 * Main component that conditionally renders WebGL animation with children
 * @param {boolean} animate - Whether to show the animation
 * @param {React.ReactNode} children - Child components to render
 * @param {boolean} onlyFrame - Whether to render only frame outline
 * @param {number} frameWidth - Width of the frame border
 * @param {Object} sx - Additional styles to apply to canvas
 */
export const AssistantAnimation = ({
  animate = false,
  children,
  onlyFrame = false,
  frameWidth = 0.0,
  sx = {},
}) => {
  return (
    <>
      {/* Conditionally render animation canvas */}
      {animate && <CanvasWithAnimation onlyFrame={onlyFrame} frameWidth={frameWidth} sx={sx} />}
      {children}
    </>
  );
};
